import { DataTable, DataTableSortStatus } from "mantine-datatable"
import React, { useEffect, useMemo, useState } from "react"
import { COLUMN_WIDTH_MIN, estimateColumnWidth } from "../utils/columnWidth"
import { Box } from "@mantine/core"
import { TablePagination } from "./TablePagination"

function displayCV(value: unknown): string {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') return value.length > 0 ? value : '-'
  return String(value)
}

export type CouponColumn = { headerName: string; field: string; width?: number }
export type CouponCell = string | number | null
export type CouponRow = Record<string, CouponCell>
export type CouponListData = { columns: CouponColumn[]; rows: CouponRow[] }

function compareCellValues(a: CouponCell, b: CouponCell): number {
  const aEmpty = a === null || a === undefined || a === ''
  const bEmpty = b === null || b === undefined || b === ''
  if (aEmpty && bEmpty) return 0
  if (aEmpty) return 1
  if (bEmpty) return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'ja')
}

interface Props {
  data: CouponListData
  fetching?: boolean
}

export const CouponListTable: React.FC<Props> = ({ data, fetching }) => {
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<CouponRow> | null>(null)

  useEffect(() => {
    setPage(1)
    setSortStatus(null)
  }, [data])

  const columnWidths = useMemo<Record<string, number>>(() => {
    const result: Record<string, number> = {}
    const sampleRows = data.rows.slice(0, 20)
    for (const col of data.columns) {
      if (typeof col.width === 'number' && col.width > 0) {
        result[col.field] = col.width
        continue
      }
      const samples = sampleRows.map((r) => r[col.field])
      result[col.field] = estimateColumnWidth(col.headerName, samples)
    }
    return result
  }, [data.rows, data.columns])

  const sortedRows = useMemo(() => {
    if (!sortStatus) return data.rows
    const { columnAccessor, direction } = sortStatus
    const sign = direction === 'desc' ? -1 : 1
    return [...data.rows].sort(
      (a, b) => sign * compareCellValues(a[columnAccessor], b[columnAccessor]),
    )
  }, [data.rows, sortStatus])

  const total = sortedRows.length
  const pageCnt = Math.max(1, Math.ceil(total / perPage))

  const tableColumns = useMemo(
    () =>
      data.columns.map((col) => ({
        accessor: col.field,
        title: col.headerName,
        sortable: true,
        resizable: true,
        ellipsis: true,
        width: columnWidths[col.field] ?? COLUMN_WIDTH_MIN,
        render: (r: CouponRow) => displayCV(r[col.field]),
      })),
    [data.columns, columnWidths],
  )

  const pagedRows = useMemo(() => {
    const from = (page - 1) * perPage
    return sortedRows.slice(from, from + perPage)
  }, [sortedRows, page, perPage])

  return (
    <Box
      style={{
        border: '1px solid #0000001A',
        borderRadius: '8px',
        overflow: 'hidden',
        minWidth: 0,
        width: '100%',
      }}
      mt={16}
    >
      <DataTable
        withColumnBorders
        fetching={fetching}
        records={pagedRows}
        noRecordsText="データがありません"
        mih={pagedRows.length === 0 ? 450 : undefined}
        styles={{
          root: { border: 'none' },
          header: {
            backgroundColor: '#00000008',
          },
        }}
        columns={tableColumns}
        sortStatus={sortStatus ?? { columnAccessor: '', direction: 'asc' }}
        onSortStatusChange={setSortStatus}
      />

      <TablePagination
        page={page}
        onChangePage={setPage}
        perPage={perPage}
        onChangePerPage={(v) => { setPerPage(v); setPage(1) }}
        totalPages={pageCnt}
        isEmpty={pagedRows.length === 0}
      />
    </Box>
  )
}
